import React, { useEffect, useRef, useState } from "react";
import {
  Badge, Box, Button, Flex, HStack, Image, Spinner, Tag, Text, useBreakpointValue,
} from "@chakra-ui/react";
import { AlertTriangle, ImageOff, RefreshCw } from "lucide-react";
import { mensagemDeErro } from "../../utils/erroApi";

export const ACCENT = "#F68633";
export const ACCENT_HOVER = "#e07728";
export const VERDE = "#01695E";
export const VERMELHO = "#C53030";
export const AZUL = "#2B6CB0";

// Cores dos gráficos (paleta categórica validada para fundo branco). A cor
// segue o significado e combina com os badges: verde = corrigido, azul =
// recriado, vermelho = falha, roxo = simulado. Validado em ordem de pilha:
// pior par vizinho ΔE 21,6 com daltonismo e 29,0 sem; contraste >= 3:1.
export const COR_SERIE = {
  corrigidos: "#008300",
  recriados: "#2a78d6",
  falhas: "#e34948",
  simulados: "#4a3aa7",
};
// Uma cor por conta, sempre a mesma (conta 1 = azul, conta 2 = laranja...).
export const COR_CONTA = ["#2a78d6", "#eb6834", "#1baf7a"];
// Barras de categorias sem ordem (motivo, cura, tipo): uma cor só.
export const COR_BARRA = "#2a78d6";

export const PERIODOS = [
  { dias: 7, label: "7 dias" },
  { dias: 30, label: "30 dias" },
  { dias: 90, label: "90 dias" },
];

// Vocabulário do backend. Os rótulos vêm em `rotulos` do GET /painel; aqui
// fica só a ordem e a cor. Chave sem rótulo aparece crua.
export const TIPOS = [
  "moderado", "pausado", "sem_anuncio", "anuncio_morto", "duplicado", "orfao_sem_estoque", "solto", "a_combinar",
];
export const CURAS = [
  "repor_estoque", "reativar", "trocar_fotos", "ampliar_foto", "recriar_categoria", "recriar_titulo",
  "completar_atributos", "excluir_duplicado", "adotar_orfao", "pausar_orfao", "criar_anuncio",
  "tentar_coleta", "revisar_preco", "aguardar", "manual",
];
export const SITUACOES = [
  "pendente", "processando", "aguardando_verificacao", "aguardando_criacao", "corrigido", "falhou",
  "manual", "ignorado", "resolvido",
];
// Sem filtro de situação, a lista do backend traz só estas (em aberto); "todas" traz tudo.
export const SITUACOES_ABERTAS = ["pendente", "processando", "aguardando_verificacao", "aguardando_criacao", "falhou", "manual"];
export const ACOES = [
  "diagnosticar", "repor_estoque", "reativar", "trocar_fotos", "ampliar_foto", "trocar_categoria", "tentar_coleta",
  "reverter_coleta", "recriar", "completar_atributos", "excluir_duplicado", "adotar_orfao", "pausar_orfao", "criar_anuncio",
  "verificar", "corrigir", "corrigir_manual", "ignorar", "reabrir",
];
export const RESULTADOS = ["ok", "falha", "simulado", "pulado"];

export const COR_SITUACAO = {
  pendente: { cor: "gray" },
  processando: { cor: "blue" },
  aguardando_verificacao: { cor: "purple" },
  aguardando_criacao: { cor: "cyan" },
  corrigido: { cor: "green" },
  falhou: { cor: "red" },
  manual: { cor: "orange" },
  ignorado: { cor: "gray", outline: true },
  resolvido: { cor: "teal" },
};

export const COR_TIPO = {
  moderado: "red",
  pausado: "yellow",
  sem_anuncio: "purple",
  anuncio_morto: "gray",
  duplicado: "orange",
  orfao_sem_estoque: "pink",
  solto: "cyan",
  a_combinar: "blue",
};

export const COR_RESULTADO = { ok: "green", falha: "red", simulado: "purple", pulado: "gray" };

// Resultado da troca de categoria para ter coleta (ajustes.coleta); o rótulo vem em rotulos.coleta.
export const COR_COLETA = {
  aplicada: "green",
  aguardando: "purple",
  revertida: "orange",
  moderada: "orange",
  sem_categoria: "gray",
  recusada: "gray",
  nao_e_categoria: "gray",
  sem_medidas: "gray",
  pacote_grande: "gray",
  frete_caro: "gray",
  sem_cotacao: "gray",
  reverter_falhou: "red",
};

// Execuções não têm rótulo no backend: tradução local.
export const EXECUCAO = {
  tipo: { varredura: "Varredura", correcao: "Correção" },
  origem: { agendado: "Agendada", manual: "Manual" },
  status: {
    em_andamento: { label: "Em andamento", cor: "blue" },
    concluida: { label: "Concluída", cor: "green" },
    falhou: { label: "Falhou", cor: "red" },
  },
};

export const FILTROS_ITENS_PADRAO = {
  conta: "", tipo: "", situacao: "", motivo: "", cura: "", busca: "", ordem: "recentes",
};

// Item ainda sem cura (a maioria dos pendentes recém-varridos).
export const SEM_CURA = "Ainda sem diagnóstico: o sistema lê o motivo no ML e decide a cura.";

// Mesmo formato que o backend aceita para o id do modelo no OpenRouter.
export const SLUG_MODELO = /^[a-z0-9][a-z0-9._-]*\/[a-z0-9][a-z0-9._:-]*$/i;

/** Rótulo vindo do backend; sem rótulo, a chave crua. */
export const rotulo = (rotulos, grupo, chave) => {
  if (chave === null || chave === undefined || chave === "") return "—";
  return rotulos?.[grupo]?.[chave] || String(chave);
};

/** Ação do histórico; registro antigo pode ter o nome da cura no lugar da ação. */
export const rotuloAcao = (rotulos, chave) => {
  if (chave === null || chave === undefined || chave === "") return "—";
  return rotulos?.acoes?.[chave] || rotulos?.curas?.[chave] || String(chave);
};

/** Nome da conta igual em toda a tela ("Conta 1 · TAGA AUTOPARTES"); sem nome, "Conta N". */
export const nomeConta = (nomes, id) => {
  if (id === null || id === undefined || id === "") return "Todas as contas";
  return nomes?.[String(id)] || `Conta ${id}`;
};

/**
 * Contas em "só pausados" (vale para o sistema todo): anúncio novo nasce pausado e nenhuma rotina
 * automática ativa anúncio delas. Vem do GET/PUT da configuração (a lista, solta ou na config, ou o
 * selo de cada conta disponível); null quando o servidor não informa.
 */
export const contasSomentePausadas = (dados) => {
  const ordenar = (ids) => ids.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  const lista = dados?.contas_somente_pausadas ?? dados?.config?.contas_somente_pausadas;
  if (Array.isArray(lista)) return ordenar(lista);
  const disponiveis = Array.isArray(dados?.contas_disponiveis) ? dados.contas_disponiveis : [];
  if (!disponiveis.some((c) => typeof c?.somente_pausados === "boolean")) return null;
  return ordenar(disponiveis.filter((c) => c.somente_pausados).map((c) => c.id));
};

export const erroMsg = (e, padrao = "Falha na operação.") => mensagemDeErro(e?.response?.data ?? e, padrao);

/** Toast de falha: 409 ("já está em andamento") é aviso, não erro. */
export const toastDeFalha = (e, titulo) => {
  if (e?.response?.status === 409) {
    return { title: erroMsg(e, "Já está em andamento."), status: "info", duration: 6000, isClosable: true };
  }
  return { title: titulo, description: erroMsg(e), status: "error", duration: 8000, isClosable: true };
};

export const num = (v, casas = 0) => {
  if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
};

export const pct = (v, casas = 1) => (v === null || v === undefined || v === "" ? "—" : `${num(v, casas)}%`);

/** Dinheiro de IA em dólar: 4 casas abaixo de US$ 1 (ex.: US$ 0,0420), 2 casas acima. */
export const usd = (v, casas) => {
  if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const c = casas ?? (n !== 0 && Math.abs(n) < 1 ? 4 : 2);
  // Espaço não separável: "US$" nunca fica numa linha e o valor na outra.
  return `US$\u00a0${n.toLocaleString("pt-BR", { minimumFractionDigits: c, maximumFractionDigits: c })}`;
};

/** "YYYY-MM-DD HH:MM:SS" -> Date. O "T" evita o Invalid Date do Safari no iOS. */
export const paraData = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** IA pausada por falta de saldo e a pausa ainda vale (mesma regra do backend: data no futuro). */
export const iaPausada = (config) => {
  if (!config?.ia_pausada_ate) return false;
  const ate = paraData(config.ia_pausada_ate);
  return !ate || ate > new Date();
};

const DATA_HORA = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/;

/** 05/10 06:12 (ou 05/10/2026 06:12 com ano). A data já vem no fuso de São Paulo. */
export const dataHoraBR = (s, { ano = false } = {}) => {
  if (!s) return "—";
  const m = DATA_HORA.exec(String(s));
  if (m && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(String(s))) {
    return `${m[3]}/${m[2]}${ano ? `/${m[1]}` : ""} ${m[4]}:${m[5]}`;
  }
  const d = paraData(s);
  if (!d) return String(s);
  const dois = (n) => String(n).padStart(2, "0");
  return `${dois(d.getDate())}/${dois(d.getMonth() + 1)}${ano ? `/${d.getFullYear()}` : ""} ${dois(d.getHours())}:${dois(d.getMinutes())}`;
};

/** 2026-10-05 -> 05/10 */
export const diaBR = (s) => {
  const [, m, d] = String(s || "").slice(0, 10).split("-");
  return d && m ? `${d}/${m}` : String(s || "—");
};

/** Duração entre duas datas do backend; sem fim, conta até agora. */
export const duracao = (inicio, fim) => {
  const a = paraData(inicio);
  const b = fim ? paraData(fim) : new Date();
  if (!a || !b) return "—";
  const s = Math.max(0, Math.round((b - a) / 1000));
  if (s < 60) return `${s} s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const resto = min % 60;
  return resto ? `${h} h ${String(resto).padStart(2, "0")} min` : `${h} h`;
};

/** Segundos -> "21 min 22 s" / "45 s" / "1 h 02 min". */
export const duracaoSegundos = (segundos) => {
  const s = Math.max(0, Math.round(Number(segundos) || 0));
  if (s < 60) return `${s} s`;
  const min = Math.floor(s / 60);
  if (min < 60) return s % 60 ? `${min} min ${s % 60} s` : `${min} min`;
  const h = Math.floor(min / 60);
  return min % 60 ? `${h} h ${String(min % 60).padStart(2, "0")} min` : `${h} h`;
};

// Chaves conhecidas do resumo das execuções, na ordem em que aparecem.
const RESUMO_ROTULOS = {
  universo: "Universo",
  ativos: "Ativos",
  pct_ativos: "Ativos (%)",
  fora_do_ar: "Fora do ar",
  itens_abertos: "Em aberto",
  novos: "Novos",
  resolvidos: "Resolvidos",
  recaidas: "Recaídas",
  pulados_em_curso: "Pulados (já em correção)",
  ml_ativos: "Ativos no ML",
  ml_pausados: "Pausados no ML",
  ml_moderados: "Moderados no ML",
  ml_a_combinar: "A combinar no ML",
  soltos: "Soltos",
  soltos_sem_sku: "Soltos sem SKU",
  criacoes_acompanhadas: "Criações acompanhadas",
  verificacoes: "Conferências",
  diagnosticados: "Diagnosticados",
  despachados: "Despachados",
  duracao_s: "Duração",
};

const valorResumo = (chave, v) => {
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return null;
    if (chave === "duracao_s") return duracaoSegundos(v);
    if (chave === "pct_ativos") return pct(v, 1);
    return num(v, Number.isInteger(v) ? 0 : 2);
  }
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (typeof v === "string") return v.trim() || null;
  return null;
};

/**
 * Resumo de uma execução (o backend manda achatado: { "por_tipo.moderado": 900 }).
 * Chaves conhecidas ganham rótulo; por_tipo.* e por_cura.* usam os rótulos do
 * backend; o que não se conhece vai para `outros` (a tela mostra em "ver tudo").
 */
export const resumoExecucao = (resumo, rotulos) => {
  const saida = { principais: [], porTipo: [], porCura: [], outros: [] };
  if (!resumo || typeof resumo !== "object" || Array.isArray(resumo)) return saida;
  const temPct = valorResumo("pct_ativos", resumo.pct_ativos) !== null;
  Object.keys(RESUMO_ROTULOS).forEach((chave) => {
    let valor = valorResumo(chave, resumo[chave]);
    if (valor === null) return;
    // "Ativos: 101.219 (79,7%)" em vez de duas entradas "Ativos".
    if (chave === "ativos" && temPct) valor = `${valor} (${valorResumo("pct_ativos", resumo.pct_ativos)})`;
    if (chave === "pct_ativos" && valorResumo("ativos", resumo.ativos) !== null) return;
    saida.principais.push({ chave, rotulo: RESUMO_ROTULOS[chave], valor });
  });
  Object.entries(resumo).forEach(([chave, v]) => {
    if (chave in RESUMO_ROTULOS) return;
    const valor = valorResumo(chave, v);
    if (valor === null) return;
    const [grupo, sub] = chave.split(".");
    if (grupo === "por_tipo" && sub) saida.porTipo.push({ chave, rotulo: rotulo(rotulos, "tipos", sub), valor });
    else if (grupo === "por_cura" && sub) saida.porCura.push({ chave, rotulo: rotulo(rotulos, "curas", sub), valor });
    else saida.outros.push({ chave, rotulo: chave, valor });
  });
  return saida;
};

/** Junta a ordem local com chaves novas que o backend mandar nos rótulos. */
const juntarChaves = (base, extra) => [...base, ...Object.keys(extra || {}).filter((k) => !base.includes(k))];

/** Listas dos filtros e nomes das contas a partir do painel (funciona com o painel nulo). */
export const montarOpcoes = (painel) => {
  const r = painel?.rotulos || {};
  // Nome de cada conta: nomes_contas do painel; senão o do card; senão "Conta N".
  const nomesContas = {};
  Object.entries(painel?.nomes_contas || {}).forEach(([id, nome]) => {
    if (nome && Number.isFinite(Number(id))) nomesContas[String(Number(id))] = nome;
  });
  (painel?.contas || []).forEach((c) => {
    const id = String(Number(c.conta_id));
    if (c.nome && !nomesContas[id]) nomesContas[id] = c.nome;
  });

  const ids = [];
  const incluir = (id) => {
    const n = Number(id);
    if (Number.isFinite(n) && !ids.includes(n)) ids.push(n);
  };
  Object.keys(nomesContas).forEach(incluir);
  (painel?.config?.contas || []).forEach(incluir);
  if (!ids.length) [1, 2, 3].forEach(incluir);
  ids.sort((a, b) => a - b);
  const contas = ids.map((id) => ({ id, nome: nomeConta(nomesContas, id) }));

  const motivos = [...new Set([
    ...(painel?.por_motivo || []).map((m) => m.motivo_codigo),
    ...Object.keys(r.motivos || {}),
  ].filter(Boolean))];

  return {
    contas,
    nomesContas,
    tipos: juntarChaves(TIPOS, r.tipos),
    curas: juntarChaves(CURAS, r.curas),
    situacoes: juntarChaves(SITUACOES, r.situacoes),
    acoes: juntarChaves(ACOES, r.acoes),
    resultados: juntarChaves(RESULTADOS, r.resultados),
    motivos,
  };
};

/** Celular e tablet em pé mostram cartões; daí para cima, tabela. */
export const useCelular = () => useBreakpointValue({ base: true, lg: false }, { ssr: false }) ?? false;

/** Ref que diz se o componente ainda está na tela (evita setState depois de sair). */
export const useMontado = () => {
  const ref = useRef(false);
  useEffect(() => {
    ref.current = true;
    return () => { ref.current = false; };
  }, []);
  return ref;
};

export function Kpi({ label, valor, ajuda, cor = VERDE }) {
  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" p={{ base: 3, md: 4 }} borderTop="3px solid" borderTopColor={cor} minW={0}>
      {/* No celular o rótulo pode ir a 2 linhas: todos reservam as 2 para os valores ficarem alinhados. */}
      <Text fontSize="xs" color="gray.600" textTransform="uppercase" fontWeight="bold" letterSpacing="wide" noOfLines={2}
        minH={{ base: "3em", sm: "auto" }}>
        {label}
      </Text>
      <Text fontSize="xl" fontWeight="bold" color={cor} lineHeight="1.2" mt={1} noOfLines={1}>
        {valor}
      </Text>
      {ajuda && <Text fontSize="xs" color="gray.600" mt={1} noOfLines={2}>{ajuda}</Text>}
    </Box>
  );
}

export function Vazio({ titulo, children, acao }) {
  return (
    <Box textAlign="center" py={10} px={4} color="gray.600">
      <Text fontWeight="bold" color="gray.700" mb={1}>{titulo}</Text>
      {children && <Text fontSize="sm" maxW="520px" mx="auto">{children}</Text>}
      {acao && <Box mt={3}>{acao}</Box>}
    </Box>
  );
}

/** Falha de carga: explica e oferece tentar de novo (nunca mostra tela vazia como se não houvesse dado). */
export function ErroCarga({ titulo = "Não foi possível carregar.", children, onTentar, carregando }) {
  return (
    <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="red.100" p={{ base: 4, md: 6 }} textAlign="center" role="alert">
      <Flex justify="center" mb={2} color={VERMELHO}><AlertTriangle size={22} aria-hidden /></Flex>
      <Text fontWeight="bold" color="gray.800">{titulo}</Text>
      <Text fontSize="sm" color="gray.600" mt={1} maxW="480px" mx="auto">
        {children || "Confira a conexão e tente de novo. Se continuar, o servidor pode estar fora do ar."}
      </Text>
      {onTentar && (
        <Button mt={4} size={{ base: "md", lg: "sm" }} variant="outline" leftIcon={<RefreshCw size={14} />} onClick={onTentar} isLoading={carregando}>
          Tentar de novo
        </Button>
      )}
    </Box>
  );
}

/** Selo da conta em "só pausados" (a Saúde atua só nos itens liberados). */
export function SeloSomentePausados(props) {
  return (
    <Badge colorScheme="orange" variant="subtle" fontSize="0.7em" whiteSpace="nowrap" {...props}>
      Só pausados
    </Badge>
  );
}

export function BadgeSituacao({ situacao, rotulos, ...rest }) {
  const s = COR_SITUACAO[situacao] || { cor: "gray" };
  return (
    <Badge colorScheme={s.cor} variant={s.outline ? "outline" : "subtle"} color={s.outline ? "gray.600" : undefined}
      fontSize="0.7em" whiteSpace="nowrap" {...rest}>
      {rotulo(rotulos, "situacoes", situacao)}
    </Badge>
  );
}

/** Tipo do problema: Tag (caixa normal) para não confundir com o badge de situação. */
export function TagTipo({ tipo, rotulos }) {
  return (
    <Tag size="sm" variant="subtle" colorScheme={COR_TIPO[tipo] || "gray"} borderRadius="full" whiteSpace="nowrap">
      {rotulo(rotulos, "tipos", tipo)}
    </Tag>
  );
}

export function BadgeResultado({ resultado, rotulos }) {
  return (
    <Badge colorScheme={COR_RESULTADO[resultado] || "gray"} fontSize="0.7em" whiteSpace="nowrap">
      {rotulo(rotulos, "resultados", resultado)}
    </Badge>
  );
}

export function Miniatura({ src, tamanho = "40px" }) {
  const [falhou, setFalhou] = useState(null);
  const mostrar = src && falhou !== src;
  return (
    <Flex w={tamanho} h={tamanho} flexShrink={0} borderRadius="md" overflow="hidden" bg="gray.100" align="center" justify="center">
      {mostrar
        ? <Image src={src} alt="" w="100%" h="100%" objectFit="cover" loading="lazy" onError={() => setFalhou(src)} />
        : <ImageOff size={16} color="#A0AEC0" aria-hidden />}
    </Flex>
  );
}

/**
 * Véu de carregamento sobre uma lista. O spinner gruda a 40% da tela para
 * continuar visível numa lista comprida (celular).
 */
export function CarregandoSobre() {
  return (
    <Box position="absolute" inset={0} bg="whiteAlpha.700" zIndex={1} borderRadius="lg">
      <Flex position="sticky" top="40vh" justify="center" py={6}><Spinner color={ACCENT} label="Carregando" /></Flex>
    </Box>
  );
}

/** `rolarPara`: ref do topo da lista; trocar de página volta para ele (lista comprida no celular). */
export function Paginacao({ pagina, porPagina, total, carregando, onPagina, rolarPara }) {
  if (!total || total <= porPagina) return null;
  const inicio = Math.min(total, (pagina - 1) * porPagina + 1);
  const fim = Math.min(total, pagina * porPagina);
  const ir = (p) => {
    onPagina(p);
    rolarPara?.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };
  return (
    <Flex justify="space-between" align="center" mt={3} gap={2} flexWrap="wrap">
      <Text fontSize="xs" color="gray.600">{num(inicio)}–{num(fim)} de {num(total)}</Text>
      <HStack>
        <Button size={{ base: "md", lg: "sm" }} variant="outline" bg="white" onClick={() => ir(pagina - 1)}
          isDisabled={pagina <= 1 || carregando}>
          Anterior
        </Button>
        <Button size={{ base: "md", lg: "sm" }} variant="outline" bg="white" onClick={() => ir(pagina + 1)}
          isDisabled={fim >= total || carregando}>
          Próxima
        </Button>
      </HStack>
    </Flex>
  );
}
